/**
 * Filters the FAQ accordion rows by category pill. Rows not matching the
 * active category are hidden via [hidden] rather than removed, so the
 * accordion-custom rows underneath keep their state/animation wiring intact.
 */
class FaqFilter extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
    this.#updateLastVisible();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
  }

  #handleClick = (/** @type {MouseEvent} */ event) => {
    const button = /** @type {HTMLElement | null} */ (event.target).closest('[data-category]');
    if (!(button instanceof HTMLElement)) return;

    const category = button.dataset.category;
    const faq = this.closest('.faq');
    if (!faq) return;

    for (const pill of this.querySelectorAll('[data-category]')) {
      pill.classList.toggle('is-active', pill === button);
    }

    for (const item of faq.querySelectorAll('.faq__item')) {
      const itemEl = /** @type {HTMLElement} */ (item);
      itemEl.hidden = category !== 'all' && itemEl.dataset.category !== category;
    }

    this.#updateLastVisible();
  };

  /**
   * Marks the last non-hidden row so its divider (added in CSS off this
   * class, not :last-child — hidden rows still count for :last-child) can be
   * removed, whichever category is currently filtered.
   */
  #updateLastVisible() {
    const faq = this.closest('.faq');
    if (!faq) return;

    const items = faq.querySelectorAll('.faq__item');
    let lastVisible = null;

    for (const item of items) {
      item.classList.remove('is-last-visible');
      if (!item.hidden) lastVisible = item;
    }

    lastVisible?.classList.add('is-last-visible');
  }
}

if (!customElements.get('faq-filter')) {
  customElements.define('faq-filter', FaqFilter);
}

/**
 * "Pills" FAQ rows: one short height + opacity animation per row. The <details>
 * stays [open] until the closing animation finishes, so the content can shrink
 * instead of snapping shut; `.is-open` tracks the logical state (it drives the
 * plus -> x rotation straight away). Only `height` is animated (no padding or
 * min-height changes) to keep each frame as cheap as possible.
 */
const PILLS_DURATION = 550;
const PILLS_EASING = 'cubic-bezier(0.45, 0, 0.25, 1)';

/**
 * @param {HTMLDetailsElement} details
 * @param {boolean} opening
 */
function animatePillsRow(details, opening) {
  const content = /** @type {HTMLElement | null} */ (details.querySelector('.details-content'));
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;

  // @ts-ignore - stored on the element between calls
  const running = details._pillsAnim;
  // Measure before cancelling, so reversing mid-way starts from where the row is now.
  const startHeight = content ? content.offsetHeight : 0;
  if (running) running.cancel();

  if (!content || reduce) {
    details.open = opening;
    details.classList.toggle('is-open', opening);
    return;
  }

  if (opening) details.open = true;
  details.classList.toggle('is-open', opening);

  const endHeight = opening ? content.scrollHeight : 0;
  const from = opening ? 0 : startHeight;
  const animation = content.animate(
    [
      { height: from + 'px', opacity: opening ? 0 : 1 },
      { height: endHeight + 'px', opacity: opening ? 1 : 0 },
    ],
    // Keep the end state while closing until [open] is removed (otherwise the
    // content flashes back to full height for a frame).
    { duration: PILLS_DURATION, easing: PILLS_EASING, fill: opening ? 'none' : 'forwards' }
  );
  // @ts-ignore
  details._pillsAnim = animation;

  animation.onfinish = () => {
    // @ts-ignore
    details._pillsAnim = null;
    if (!opening) {
      details.open = false;
      animation.cancel();
    }
  };
  animation.oncancel = () => {
    // @ts-ignore
    details._pillsAnim = null;
  };
}

document.addEventListener(
  'click',
  (event) => {
    const target = /** @type {Element | null} */ (event.target);
    const summary = target?.closest('.faq--pills summary');
    if (!summary) return;

    const details = summary.parentElement;
    if (!(details instanceof HTMLDetailsElement)) return;

    // Take over from the native toggle so we can animate it.
    event.preventDefault();

    const opening = !details.classList.contains('is-open');

    if (opening) {
      const card = details.closest('.faq__card');
      for (const other of card?.querySelectorAll('details.is-open') ?? []) {
        if (other !== details && other instanceof HTMLDetailsElement) animatePillsRow(other, false);
      }
    }

    animatePillsRow(details, opening);
  },
  true
);

/**
 * Adds one FAQPage JSON-LD block for all FAQ rows on the page. The rows are theme
 * blocks, which Liquid can't read from the section, so the schema is built from
 * the rendered markup. Every FAQ section on a page contributes to the same block.
 */
function injectFaqSchema() {
  if (document.querySelector('script[data-faq-schema]')) return;

  const entities = [];
  for (const item of document.querySelectorAll('.faq__item')) {
    const summary = item.querySelector('summary');
    const answerEl = item.querySelector('.details-content');
    if (!summary || !answerEl) continue;

    const summaryClone = /** @type {HTMLElement} */ (summary.cloneNode(true));
    for (const icon of summaryClone.querySelectorAll('.svg-wrapper')) icon.remove();

    const question = summaryClone.textContent?.replace(/\s+/g, ' ').trim();
    const answer = answerEl.textContent?.replace(/\s+/g, ' ').trim();
    if (!question || !answer) continue;

    entities.push({
      '@type': 'Question',
      name: question,
      acceptedAnswer: { '@type': 'Answer', text: answer },
    });
  }

  if (entities.length === 0) return;

  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.dataset.faqSchema = '';
  script.textContent = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: entities,
  });
  document.head.append(script);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', injectFaqSchema, { once: true });
} else {
  injectFaqSchema();
}
